import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@softline/core';
import { Router } from '@angular/router';
import {
  BackNavigationService,
  HeaderComponent,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL } from '../../lagerkorrektur-artikel.shared';
import { LagerkorrekturArtikelStore } from '../../store';
import {
  ArtikelMenge,
  ArtikelMengeAuswahlComponent,
  openSelectUnitDialog,
} from '@softapps/lager/core';
import { SearchInputComponent, TranslatePipe } from '@softline/ui-core';
import { type ProgressStep, ProgressStepsComponent } from '@softline/ui-core/progress';
import {
  PageComponent,
  PageHeaderComponent,
  Panel,
  PanelHeaderComponent,
} from '@softapps/core/pages';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatestWith, debounceTime, map, startWith, Subject } from 'rxjs';

@Component({
  selector: 'soft-artikel-auswahl',
  imports: [
    CommonModule,
    ProgressStepsComponent,
    ArtikelMengeAuswahlComponent,
    HeaderComponent,
    TranslatePipe,
    PageComponent,
    PageHeaderComponent,
    Panel,
    PanelHeaderComponent,
    SearchInputComponent,
  ],
  templateUrl: './artikel-auswahl.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./artikel-auswahl.page.css'],
})
export class ArtikelAuswahlPage implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly backNavigationService = inject(BackNavigationService);

  readonly searchValue$ = new Subject<string | null>();

  private readonly openSelectDialog = openSelectUnitDialog();

  lagerplatz$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
    LagerkorrekturArtikelStore.getters.lagerplatz
  );

  readonly steps: ProgressStep[] = [
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.LAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.LAGERPLATZ_SCANNEN.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.ARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.ARTIKEL_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.MENGEN_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.MENGEN_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.UMBUCHUNGSARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.UMBUCHUNGSARTIKEL_AUSWAHL.DESCRIPTION',
    },
  ];

  protected readonly filteredStorageLocationData = toSignal(
    this.searchValue$.pipe(
      startWith(null),
      debounceTime(350),
      combineLatestWith(this.lagerplatz$),
      map(([searchValue, storageLocation]) => {
        const inhalt = storageLocation?.inhalt ?? [];
        const search = searchValue?.trim().toLowerCase();
        if (!search) return inhalt;

        return inhalt.filter((o) =>
          Object.values(o.artikel ?? {}).some(
            (value) =>
              value !== null &&
              value !== undefined &&
              String(value).toLowerCase().includes(search)
          )
        );
      })
    )
  );

  constructor() {
    //
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#LAGERKORREKTUR_ARTIKEL.TITLE'
    );
  }

  ngOnDestroy() {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/lagerkorrektur-artikel']);
  }

  async onSelect(artikel: ArtikelMenge): Promise<void> {
    let selectedArtikel = { ...artikel };

    if (
      artikel.artikel.kzartikelart === 'DIF' ||
      artikel.artikel.kzartikelart === 'SDIF'
    ) {
      const result = await this.openSelectDialog(artikel.einheitenAuswahl ?? []);
      if (!result || result === 'DISMISSED') return;
      selectedArtikel = {
        ...selectedArtikel,
        einheit: result.einheit,
        menge: result.menge,
      };
    }

    this.store.commit(
      SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      LagerkorrekturArtikelStore.mutations.setArtikel,
      selectedArtikel
    );
    await this.router.navigate(['/lagerkorrektur-artikel', 'mengen-auswahl']);
  }
}
